import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Download, ExternalLink, FileText, ChevronLeft, ChevronRight } from "lucide-react";

/** Previsualizador in-page de adjuntos (PDFs, imágenes y otros).
 *  Recibe la lista completa de adjuntos para navegar entre ellos con flechas. */
export default function AttachmentPreview({ items, index, onOpenChange, onIndexChange }) {
  const att = items?.[index];
  const open = att != null;
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);

  const loadAttachment = async (fileId, contentType, signal) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/attachments/${fileId}`, { responseType: "blob" });
      if (signal.aborted) return null;
      const url = URL.createObjectURL(new Blob([data], { type: contentType || "application/octet-stream" }));
      setBlobUrl(url);
      return url;
    } catch {
      toast.error("No se pudo cargar el adjunto");
      onOpenChange(false);
      return null;
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  };

  // Carga el blob cuando cambia el adjunto activo
  useEffect(() => {
    if (!att?.file_id) { setBlobUrl(null); return undefined; }
    const ctrl = new AbortController();
    let createdUrl = null;
    loadAttachment(att.file_id, att.content_type, ctrl.signal).then((u) => { createdUrl = u; });
    return () => {
      ctrl.abort();
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [att?.file_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const ct = (att?.content_type || "").toLowerCase();
  const isPdf = ct.includes("pdf");
  const isImg = ct.startsWith("image/");
  const canPreview = isPdf || isImg;
  const total = items?.length || 0;
  const go = (delta) => {
    const next = (index + delta + total) % total;
    onIndexChange(next);
  };

  const download = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = att.filename || "adjunto";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl w-[90vw] h-[88vh] p-0 flex flex-col overflow-hidden"
        data-testid="attachment-preview-dialog"
      >
        <DialogTitle className="sr-only">{att?.filename || "Adjunto"}</DialogTitle>
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-200 bg-gray-50">
          <FileText size={15} className="text-blue-600 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">{att?.filename || "Adjunto"}</div>
            <div className="text-[11px] text-gray-500">
              {att?.content_type || ""} {att?.size != null && `· ${(att.size / 1024).toFixed(0)} KB`}
              {total > 1 && ` · ${index + 1} / ${total}`}
            </div>
          </div>
          {total > 1 && (
            <>
              <button onClick={() => go(-1)} className="p-1.5 rounded hover:bg-gray-200 text-gray-600"
                title="Anterior" data-testid="attachment-preview-prev"><ChevronLeft size={16} /></button>
              <button onClick={() => go(1)} className="p-1.5 rounded hover:bg-gray-200 text-gray-600"
                title="Siguiente" data-testid="attachment-preview-next"><ChevronRight size={16} /></button>
            </>
          )}
          {blobUrl && (
            <>
              <button onClick={download} className="p-1.5 rounded hover:bg-gray-200 text-gray-600"
                title="Descargar" data-testid="attachment-preview-download"><Download size={16} /></button>
              <a href={blobUrl} target="_blank" rel="noreferrer"
                className="p-1.5 rounded hover:bg-gray-200 text-gray-600"
                title="Abrir en pestaña nueva"><ExternalLink size={16} /></a>
            </>
          )}
        </div>
        <div className="flex-1 bg-gray-100 overflow-auto flex items-center justify-center">
          {loading && <Loader2 className="animate-spin text-gray-400" size={28} />}
          {!loading && blobUrl && isPdf && (
            <iframe src={blobUrl} title={att.filename} className="w-full h-full border-0 bg-white"
              data-testid="attachment-preview-pdf" />
          )}
          {!loading && blobUrl && isImg && (
            <img src={blobUrl} alt={att.filename} className="max-w-full max-h-full object-contain"
              data-testid="attachment-preview-image" />
          )}
          {!loading && blobUrl && !canPreview && (
            <div className="text-center space-y-3 p-8">
              <FileText size={40} className="text-gray-400 mx-auto" />
              <p className="text-sm text-gray-700">No hay vista previa disponible para este tipo de archivo.</p>
              <button onClick={download}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700">
                <Download size={13} /> Descargar
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
